/*******************************************************************************
 *
 * AWS CDK CLI Enums
 *
 * `as const` enums for the `cdk` CLI surface. Phase 1 covered `cdk deploy`;
 * Phase 2 adds the enum needed by `cdk diff`; Phase 3 adds the enums needed by
 * `cdk gc`; Phase 4 adds the enums needed by `cdk init` (and reused by `cdk
 * migrate`); Phase 5 brings the surface up to aws-cdk CLI 2.1136.0 (the pinned
 * version) plus the additions that landed in 2.1137/2.1138.
 *
 ******************************************************************************/

/**
 * `--require-approval` values accepted by `cdk deploy`.
 */
export const CDK_REQUIRE_APPROVAL = {
  Never: "never",
  AnyChange: "any-change",
  Broadening: "broadening",
} as const;

/**
 * Type for {@link CDK_REQUIRE_APPROVAL} values.
 */
export type CdkRequireApproval =
  (typeof CDK_REQUIRE_APPROVAL)[keyof typeof CDK_REQUIRE_APPROVAL];

/**
 * `--method` values accepted by `cdk deploy`.
 */
export const CDK_DEPLOY_METHOD = {
  ChangeSet: "change-set",
  Direct: "direct",
  PrepareChangeSet: "prepare-change-set",
  ExecuteChangeSet: "execute-change-set",
} as const;

/**
 * Type for {@link CDK_DEPLOY_METHOD} values.
 */
export type CdkDeployMethod =
  (typeof CDK_DEPLOY_METHOD)[keyof typeof CDK_DEPLOY_METHOD];

/**
 * `--progress` values accepted by `cdk deploy` and `cdk watch`.
 *
 * `ErrorsOnly` displays nothing except errors — upstream recommends it for AI
 * agents and other consumers that do not benefit from continuous progress
 * updates.
 *
 * **CLI version note:** `ErrorsOnly` requires aws-cdk CLI **2.1137.0 or
 * later**. This repository currently pins 2.1136.0, which rejects
 * `--progress=errors-only`. Consumers on the pinned version should stay on
 * `Bar` or `Events` until the pin advances.
 */
export const CDK_PROGRESS = {
  Bar: "bar",
  Events: "events",
  ErrorsOnly: "errors-only",
} as const;

/**
 * Type for {@link CDK_PROGRESS} values.
 */
export type CdkProgress = (typeof CDK_PROGRESS)[keyof typeof CDK_PROGRESS];

/**
 * `--method` values accepted by `cdk diff`.
 */
export const CDK_DIFF_METHOD = {
  Auto: "auto",
  ChangeSet: "change-set",
  Template: "template",
} as const;

/**
 * Type for {@link CDK_DIFF_METHOD} values.
 */
export type CdkDiffMethod =
  (typeof CDK_DIFF_METHOD)[keyof typeof CDK_DIFF_METHOD];

/**
 * `--action` values accepted by `cdk gc`.
 */
export const CDK_GC_ACTION = {
  DeleteTagged: "delete-tagged",
  Full: "full",
  Print: "print",
  Tag: "tag",
} as const;

/**
 * Type for {@link CDK_GC_ACTION} values.
 */
export type CdkGcAction = (typeof CDK_GC_ACTION)[keyof typeof CDK_GC_ACTION];

/**
 * `--type` values accepted by `cdk gc`.
 */
export const CDK_GC_TYPE = {
  All: "all",
  Ecr: "ecr",
  S3: "s3",
} as const;

/**
 * Type for {@link CDK_GC_TYPE} values.
 */
export type CdkGcType = (typeof CDK_GC_TYPE)[keyof typeof CDK_GC_TYPE];

/**
 * Positional `<template>` values accepted by `cdk init`.
 */
export const CDK_INIT_TEMPLATE = {
  App: "app",
  Lib: "lib",
  SampleApp: "sample-app",
} as const;

/**
 * Type for {@link CDK_INIT_TEMPLATE} values.
 */
export type CdkInitTemplate =
  (typeof CDK_INIT_TEMPLATE)[keyof typeof CDK_INIT_TEMPLATE];

/**
 * `--language` values accepted by `cdk init`. Also reused by `cdk migrate`'s
 * `--language` flag, which accepts the same set of language identifiers.
 */
export const CDK_INIT_LANGUAGE = {
  CSharp: "csharp",
  FSharp: "fsharp",
  Go: "go",
  Java: "java",
  JavaScript: "javascript",
  Python: "python",
  TypeScript: "typescript",
} as const;

/**
 * Type for {@link CDK_INIT_LANGUAGE} values.
 */
export type CdkInitLanguage =
  (typeof CDK_INIT_LANGUAGE)[keyof typeof CDK_INIT_LANGUAGE];

/**
 * `--package-manager` values accepted by `cdk init`.
 *
 * Only applicable to TypeScript and JavaScript projects; the CLI defaults to
 * `npm` for those and ignores the flag for every other language.
 */
export const CDK_INIT_PACKAGE_MANAGER = {
  Npm: "npm",
  Yarn: "yarn",
  Pnpm: "pnpm",
  Bun: "bun",
} as const;

/**
 * Type for {@link CDK_INIT_PACKAGE_MANAGER} values.
 */
export type CdkInitPackageManager =
  (typeof CDK_INIT_PACKAGE_MANAGER)[keyof typeof CDK_INIT_PACKAGE_MANAGER];

/**
 * `--from-scan` values accepted by `cdk migrate` — whether to start a new
 * CloudFormation resource scan or reuse the last successful one.
 *
 * Upstream declares `--from-scan` as a free-form `string` option and documents
 * the two accepted values only in the flag description rather than as a yargs
 * `choices` array. This enum pins that documented pair so callers cannot
 * render an invocation the CLI will reject.
 */
export const CDK_MIGRATE_FROM_SCAN = {
  New: "new",
  MostRecent: "most-recent",
} as const;

/**
 * Type for {@link CDK_MIGRATE_FROM_SCAN} values.
 */
export type CdkMigrateFromScan =
  (typeof CDK_MIGRATE_FROM_SCAN)[keyof typeof CDK_MIGRATE_FROM_SCAN];
